import React from 'react';
import ProductCard from './ProductCard';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductGrid = ({ 
  products, 
  loading, 
  onQuickView, 
  onAddToWishlist, 
  onAddToComparison,
  wishlistItems,
  comparisonItems,
  sortBy,
  onSortChange,
  viewMode,
  onViewModeChange,
  currentPage,
  totalPages,
  onPageChange
}) => {
  const sortOptions = [
    { value: 'relevance', label: 'Mais Relevantes' },
    { value: 'price-low', label: 'Menor Preço' },
    { value: 'price-high', label: 'Maior Preço' },
    { value: 'newest', label: 'Mais Recentes' },
    { value: 'popular', label: 'Mais Populares' },
    { value: 'rating', label: 'Melhor Avaliados' }
  ];

  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 gap-6">
      {[...Array(9)]?.map((_, index) => (
        <div key={index} className="bg-card rounded-xl border border-border overflow-hidden animate-pulse">
          <div className="aspect-[4/3] bg-muted"></div>
          <div className="p-4 space-y-3">
            <div className="flex justify-between">
              <div className="h-3 bg-muted rounded w-20"></div>
              <div className="h-3 bg-muted rounded w-16"></div>
            </div>
            <div className="h-5 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-full"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
            <div className="flex space-x-2">
              <div className="h-8 bg-muted rounded flex-1"></div>
              <div className="h-8 bg-muted rounded flex-1"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-6">
        <Icon name="Search" size={32} className="text-text-secondary" />
      </div>
      <h3 className="font-playfair font-semibold text-xl text-text-primary mb-2">
        Nenhum produto encontrado
      </h3>
      <p className="text-text-secondary mb-6 max-w-md">
        Não encontramos produtos que correspondam aos seus filtros. Tente ajustar os critérios de busca ou limpar os filtros.
      </p>
      <Button variant="outline" iconName="RotateCcw" iconPosition="left">
        Limpar Filtros
      </Button>
    </div>
  );

  const Pagination = () => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      
      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          pages?.push(i);
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) {
            pages?.push(i);
          }
          pages?.push('...');
          pages?.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages?.push(1);
          pages?.push('...');
          for (let i = totalPages - 3; i <= totalPages; i++) {
            pages?.push(i);
          }
        } else {
          pages?.push(1);
          pages?.push('...');
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages?.push(i);
          }
          pages?.push('...');
          pages?.push(totalPages);
        }
      }
      
      return pages;
    };

    return (
      <div className="flex items-center justify-center space-x-2 mt-12">
        <Button
          variant="outline"
          size="sm"
          iconName="ChevronLeft"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          Anterior
        </Button>
        {getPageNumbers()?.map((page, index) => (
          <React.Fragment key={index}>
            {page === '...' ? (
              <span className="px-3 py-2 text-text-secondary">...</span>
            ) : (
              <Button
                variant={currentPage === page ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(page)}
                className={currentPage === page ? "bg-primary text-primary-foreground" : ""}
              >
                {page}
              </Button>
            )}
          </React.Fragment>
        ))}
        <Button
          variant="outline"
          size="sm"
          iconName="ChevronRight"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          Próxima
        </Button>
      </div>
    );
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!products || products?.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-border">
        <div className="flex items-center space-x-4">
          <span className="text-sm text-text-secondary">
            {products?.length} produtos encontrados
          </span>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Sort Dropdown */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-text-secondary whitespace-nowrap">Ordenar por:</span>
            <select
              value={sortBy}
              onChange={(e) => onSortChange(e?.target?.value)}
              className="px-3 py-1 border border-border rounded-lg text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              {sortOptions?.map(option => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* Products Grid */}
      <div className={`grid gap-4 ${
        viewMode === 'grid' 
          ? 'grid-cols-1 sm:grid-cols-2' 
          : 'grid-cols-1'
      }`}>
        {products?.map((product) => (
          <ProductCard
            key={product?.id}
            product={product}
            onQuickView={onQuickView}
            onAddToWishlist={onAddToWishlist}
            onAddToComparison={onAddToComparison}
            isInWishlist={wishlistItems?.includes(product?.id)}
            isInComparison={comparisonItems?.includes(product?.id)}
          />
        ))}
      </div>
      {/* Pagination */}
      <Pagination />
    </div>
  );
};

export default ProductGrid;